import { Popup } from '@paywithextend/components'
import { spacing } from 'ext-ui/style/spacing'
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import ChurnSurveyScreen from 'src/component-modal/ChurnSurveyModal/ChurnSurveyModal'
import { useChurnSurveySubmit } from 'src/component-modal/ChurnSurveyModal/useChurnSurveySubmit'
import {
  deriveBillingCycleEnd,
  deriveBillingCycleEndShort,
} from 'src/components/CancelSubscription/billingCycleEnd'
import CancelSubscriptionConfirm from 'src/components/CancelSubscription/CancelSubscriptionConfirm'
import CancelSubscriptionDowngrade from 'src/components/CancelSubscription/CancelSubscriptionDowngrade'
import CancelSubscriptionInfo from 'src/components/CancelSubscription/CancelSubscriptionInfo'
import CancelSubscriptionSubmission from 'src/components/CancelSubscription/CancelSubscriptionSubmission'
import { connectedReplace } from 'src/navigation/Lnk'
import { manageSubscriptionUrl } from 'src/navigation/urls'
import {
  useCancelSubscription,
  useQueryOrganizationSaasTier,
  useQueryOrganizationSubscription,
  useQueryOrganizationUsages,
} from 'src/queries/organizations/saas.queries'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { selectUserExplicitOrganizationListModelFromState } from 'src/redux/selectors/data/organizations.selectors'
import { css } from 'styled-components'

export default function CancelSubscriptionPage(): ReactElement {
  const dispatch = useDispatch()
  const id = useSelector(selectUserListModelFromState)?.organizationId
  const { analytics } = useAnalyticsContext()

  const { mutate: cancelSubscription } = useCancelSubscription()
  const orgId = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )?.id

  const { data: currentPlan } = useQueryOrganizationSaasTier({
    orgId: orgId || '',
    enabled: !!orgId,
  })

  const { enable_downgrade_card_limit_modal: showDowngradeCardLimitModal } =
    useSelector(selectFeatureFlaggerFromState) || {}

  // Shared fetches lifted out of the step components so both screens render
  // the same billingCycleEnd and the usages query has a single observer.
  const { data: orgSubscription } = useQueryOrganizationSubscription({ orgId })
  const { data: orgUsages } = useQueryOrganizationUsages({
    orgId,
    enabled: !!orgId && !!showDowngradeCardLimitModal,
    // Manage Cards link opens /virtual-cards in a new tab. Cancels there
    // invalidate React Query in that tab only — this tab's cache is a
    // separate runtime. Refetch on focus so the meter catches up when the
    // user returns to the modal tab.
    refetchOnWindowFocus: true,
  })
  const billingCycleEnd = deriveBillingCycleEnd(
    orgSubscription?.subscription?.state,
  )
  const billingCycleEndShort = deriveBillingCycleEndShort(
    orgSubscription?.subscription?.state,
  )
  const usage = orgUsages?.features.ACTIVE_VIRTUAL_CARD_LIMIT?.usage

  type CancelSubscriptionScreen = 'info' | 'submission' | 'survey'
  const [currentScreen, setCurrentScreen] =
    useState<CancelSubscriptionScreen>('info')

  const leaveCancelSubscriptionModal = useCallback((): void => {
    if (id) {
      dispatch(connectedReplace(manageSubscriptionUrl(id)))
    }
  }, [id, dispatch])

  const handleSurveySubmit = useChurnSurveySubmit(orgId)

  useEffect(() => {
    switch (currentScreen) {
      case 'info':
        analytics.track('cancel subscription', { action: 'info_viewed' })
        break
      case 'submission':
        analytics.track('cancel subscription', { action: 'submission_viewed' })
        break
      case 'survey':
        analytics.track('churn survey', { action: 'viewed' })
        break
    }
  }, [currentScreen, analytics])

  const handleAbandon = useCallback(
    (step: 'info' | 'submission'): void => {
      analytics.track('cancel subscription', {
        action: 'abandoned',
        abandon_step: step,
      })
      leaveCancelSubscriptionModal()
    },
    [analytics, leaveCancelSubscriptionModal],
  )

  const handlePopupClose = useCallback((): void => {
    switch (currentScreen) {
      case 'info':
        handleAbandon('info')
        break
      case 'submission':
        handleAbandon('submission')
        break
      case 'survey':
        leaveCancelSubscriptionModal()
        break
    }
  }, [currentScreen, handleAbandon, leaveCancelSubscriptionModal])

  const handleConfirmDowngrade = useCallback((): void => {
    if (!orgId) return
    analytics.track('cancel subscription', {
      action: 'confirmed',
      tier: currentPlan?.tier,
    })
    cancelSubscription({
      orgId,
      tier: currentPlan?.tier,
      body: {
        intention: 'cancel_subscription',
      },
    })
    setCurrentScreen('survey')
  }, [orgId, analytics, currentPlan, cancelSubscription])

  const currentScreenComponent = useMemo(() => {
    switch (currentScreen) {
      case 'info':
        return showDowngradeCardLimitModal ? (
          <CancelSubscriptionDowngrade
            onConfirm={(): void => setCurrentScreen('submission')}
            onCancel={(): void => handleAbandon('info')}
            billingCycleEnd={billingCycleEnd}
            billingCycleEndShort={billingCycleEndShort}
            usage={usage}
          />
        ) : (
          <CancelSubscriptionInfo
            onConfirm={(): void => setCurrentScreen('submission')}
            onCancel={(): void => handleAbandon('info')}
          />
        )
      case 'submission':
        return showDowngradeCardLimitModal ? (
          <CancelSubscriptionConfirm
            onConfirm={handleConfirmDowngrade}
            onCancel={(): void => handleAbandon('submission')}
            billingCycleEnd={billingCycleEnd}
            usage={usage}
          />
        ) : (
          <CancelSubscriptionSubmission
            onConfirm={handleConfirmDowngrade}
            onCancel={(): void => handleAbandon('submission')}
          />
        )
      case 'survey':
        return (
          <ChurnSurveyScreen
            onSubmit={handleSurveySubmit}
            onSkip={leaveCancelSubscriptionModal}
          />
        )
    }
  }, [
    currentScreen,
    showDowngradeCardLimitModal,
    billingCycleEnd,
    billingCycleEndShort,
    usage,
    handleConfirmDowngrade,
    handleSurveySubmit,
    handleAbandon,
    leaveCancelSubscriptionModal,
  ])
  return (
    <Popup
      showCloseButton
      onClickClose={handlePopupClose}
      noPadding
      css={css`
        overflow: clip;
      `}
    >
      <div
        css={css`
          padding: ${spacing(10)};
        `}
      >
        {currentScreenComponent}
      </div>
    </Popup>
  )
}
